import * as React from 'react';
import { actions } from 'react-redux-form';
import * as validator from 'validator';
import { Form, Card, Notification, TypeForm } from '../';
import fields from '../../data/fields';

interface Props {
  model: any;
  fields: any;
  values: any;
  id: number;
  onUpdate: Function;
  dispatch: Function;
  item: any;
  disable: boolean;
  showHeading: boolean;
  isTypeForm?: boolean;
  meta?: any;
}

interface State {
  onChange: Function;
  onReset: Function;
  onSubmit: Function;
  name: string;
  action: string;
  type: string;
  isTypeForm: boolean;
  showSkipButton: boolean;
  updateField?: Function;
  errors: string[];
}

class BaseForm<T extends Props = Props> extends React.Component<T, State> {
  protected static defaultProps: any = {
    model: {},
    values: {},
    onUpdate: () => {},
    showHeading: true,
  };

  static fields = fields;
  static validator = validator;

  /**
   * Initialise the form
   * @param {Object} props
   * @param {Object} options
   */
  constructor(props, options) {
    super(props);
    this.state = {
      onChange: options.onChange || false,
      onReset: () => {},
      onSubmit: options.onSubmit,
      name: options.name,
      action: options.action,
      type: options.type,
      isTypeForm:
        props.isTypeForm !== undefined ? props.isTypeForm : options.isTypeForm,
      showSkipButton: options.showSkipButton || false,
      errors: [],
    };
    this.handleValidate = this.handleValidate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleFormDispatch = this.handleFormDispatch.bind(this);
  }

  /**
   * Handler for validation
   * @param {Object} fields
   * @param {Function} isIndex
   * @return {Object|Boolean}
   */
  handleValidate(fields, isIndex?) {
    throw new Error('The method handleValidate() was not overridden');
  }

  /**
   * Handler for field change
   * @param {Object} data
   */
  handleChange(data) {
    if (this.state.onChange) {
      this.state.onChange(data, this.state.updateField);
    }
  }

  /**
   * Handler for validation
   * @param {Function} dispatch
   * @param {Object} actions
   */
  handleReset(dispatch, actions) {
    throw new Error('The method handleReset() was not overridden');
  }

  /**
   * Handler for submission of form data
   * @param {Object} data
   */
  handleSubmit(data) {
    const validatedData = this.handleValidate(data);
    if (validatedData as any) {
      const onSubmit = () => {
        switch (this.state.type) {
          case 'create':
            return this.props.dispatch(this.state.onSubmit(validatedData));
          case 'update':
            return this.props.dispatch(
              this.state.onSubmit(this.props.id, validatedData),
            );
          default:
            return Promise.resolve();
        }
      };
      onSubmit().then((response) => {
        if (!response.error) {
          this.props.onUpdate(response);
          this.state.onReset();
        }
      });
    }
  }

  /**
   * Handler for instantiation between form and component state
   * @param {Object} data
   */
  handleFormDispatch(dispatch) {
    this.setState(
      {
        onReset: () => {
          this.handleReset(dispatch, actions);
        },
        updateField: (field, value) => {
          dispatch(actions.change(field, value));
        },
      },
      () => {
        this.state.onReset();
      },
    );
  }

  render() {
    return (
      <>
        <Card>
          {this.props.showHeading && (
            <h4 className="heading">
              {this.state.action} {this.state.name}
            </h4>
          )}
          {this.state.isTypeForm ? (
            <TypeForm
              fields={this.props.fields}
              type={this.state.type}
              handleChange={this.handleChange}
              handleSubmit={this.handleSubmit}
              buttonText={this.state.action}
              getDispatch={this.handleFormDispatch}
              disabled={
                (this.props.model as any).isFetching || this.props.disable
              }
              values={this.props.values}
              validate={this.handleValidate}
              showSkipButton={this.state.showSkipButton}
              resetOnSubmit
            />
          ) : (
            <Form
              fields={this.props.fields}
              type={this.state.type}
              handleChange={this.handleChange}
              handleSubmit={this.handleSubmit}
              buttonText={this.state.action}
              getDispatch={this.handleFormDispatch}
              errors={this.state.errors}
              disabled={
                (this.props.model as any).isFetching || this.props.disable
              }
              values={this.props.values}
              hasButton={!this.props.disable}
            />
          )}
        </Card>
        {(this.props.model as any) &&
          (this.props.model as any).didInvalidate && (
            <Notification type="danger">
              {(this.props.model as any).error}
            </Notification>
          )}
      </>
    );
  }
}

export default BaseForm;
